import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  EventAchievementOption,
  EventRewardType,
  EventSubmission,
  EventSubmissionState,
  EventSummary,
  EventTaskType,
} from '../../events/types';
import { API_BASE, getHttpErrorStatus, logHttpError, api } from '../../services/api';

type PanelMode = 'manage' | 'review';
type LifecycleAction = 'publish' | 'close' | 'cancel' | 'delete';

interface EventFormValues {
  title: string;
  description: string;
  instructions: string;
  cover_url: string | null;
  task_type: EventTaskType;
  start_at: string;
  end_at: string;
  reward_type: EventRewardType;
  reward_amount: string;
  reward_achievement_id: string;
}

const taskTypes: Array<{ value: EventTaskType; label: string }> = [
  { value: 'text', label: 'Текстовое задание' },
  { value: 'file', label: 'Файловое задание' },
  { value: 'confirmation', label: 'Подтверждение выполнения' },
];

const eventStates: Record<EventSummary['state'], string> = {
  draft: 'Черновик',
  published: 'Опубликовано',
  closed: 'Закрыто',
  cancelled: 'Отменено',
};

const submissionStates: Record<EventSubmissionState, string> = {
  draft: 'Черновик',
  submitted: 'На проверке',
  accepted: 'Принято',
  rejected: 'Отклонено',
};

const emptyForm = (): EventFormValues => ({
  title: '', description: '', instructions: '', cover_url: null, task_type: 'text',
  start_at: '', end_at: '', reward_type: 'coins', reward_amount: '', reward_achievement_id: '',
});

const twoDigits = (value: number) => value.toString().padStart(2, '0');

export const formatIsoForDateTimeLocal = (value: string) => {
  const instant = new Date(value);
  return `${instant.getFullYear()}-${twoDigits(instant.getMonth() + 1)}-${twoDigits(instant.getDate())}T${twoDigits(instant.getHours())}:${twoDigits(instant.getMinutes())}`;
};

export const parseDateTimeLocalToUtc = (value: string) => new Date(value).toISOString();

const fromEvent = (event: EventSummary): EventFormValues => ({
  title: event.title,
  description: event.description,
  instructions: event.instructions,
  cover_url: event.cover_url,
  task_type: event.task_type,
  start_at: formatIsoForDateTimeLocal(event.start_at),
  end_at: formatIsoForDateTimeLocal(event.end_at),
  reward_type: event.reward_type,
  reward_amount: event.reward_amount?.toString() ?? '',
  reward_achievement_id: event.reward_achievement_id?.toString() ?? '',
});

const detailFromError = (error: unknown, fallback: string) => {
  const status = getHttpErrorStatus(error);
  const detail = typeof error === 'object' && error !== null && 'response' in error
    ? (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
    : undefined;
  const formattedDetail = typeof detail === 'string'
    ? detail
    : Array.isArray(detail)
      ? detail.map((entry) => {
        if (typeof entry !== 'object' || entry === null) return null;
        const item = entry as { loc?: unknown; msg?: unknown };
        const location = Array.isArray(item.loc) ? item.loc.map(String).join('.') : '';
        const message = typeof item.msg === 'string' ? item.msg : '';
        return message ? (location ? `${location}: ${message}` : message) : null;
      }).filter((message): message is string => Boolean(message)).join('; ')
      : '';
  return (status === 403 || status === 409 || status === 422) && formattedDetail ? formattedDetail : fallback;
};

const formatDate = (value: string | null) => value ? new Date(value).toLocaleString('ru-RU') : '—';
const isImage = (url: string) => /\.(png|jpe?g|gif|webp)$/i.test(url);

const EventsPanel = () => {
  const [mode, setMode] = useState<PanelMode>('manage');
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [form, setForm] = useState<EventFormValues>(emptyForm);
  const [editingEvent, setEditingEvent] = useState<EventSummary | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverError, setCoverError] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [achievements, setAchievements] = useState<EventAchievementOption[]>([]);
  const [achievementsLoaded, setAchievementsLoaded] = useState(false);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [achievementsError, setAchievementsError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ action: LifecycleAction; event: EventSummary } | null>(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [reviewState, setReviewState] = useState<EventSubmissionState>('submitted');
  const [reviewEventId, setReviewEventId] = useState('');
  const [submissions, setSubmissions] = useState<EventSubmission[]>([]);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<number, string>>({});
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [acceptedIds, setAcceptedIds] = useState<Set<number>>(() => new Set());
  const mountedRef = useRef(false);
  const reviewRequestIdRef = useRef(0);
  const reviewFilterRef = useRef('');

  reviewFilterRef.current = `${reviewState}:${reviewEventId}`;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      reviewRequestIdRef.current += 1;
    };
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const response = await api.get<EventSummary[]>('/events/admin/events');
      setEvents(response.data);
    } catch (error: unknown) {
      logHttpError('Ошибка загрузки событий для администрации', error);
      setEvents([]);
      setEventsError(detailFromError(error, 'Не удалось загрузить события.'));
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const loadReviewQueue = useCallback(async () => {
    const requestedState = reviewState;
    const requestedEventId = reviewEventId;
    const requestedFilter = `${requestedState}:${requestedEventId}`;
    const requestId = reviewRequestIdRef.current + 1;
    reviewRequestIdRef.current = requestId;
    const requestIsCurrent = () => mountedRef.current
      && reviewFilterRef.current === requestedFilter
      && reviewRequestIdRef.current === requestId;
    const suffix = requestedEventId ? `&event_id=${requestedEventId}` : '';

    if (!requestIsCurrent()) return;
    try {
      setReviewLoading(true);
      setReviewError(null);
      const response = await api.get<EventSubmission[]>(`/events/admin/submissions?state=${requestedState}${suffix}`);
      if (!requestIsCurrent()) return;
      setSubmissions(response.data);
    } catch (error: unknown) {
      if (!requestIsCurrent()) return;
      logHttpError('Ошибка загрузки очереди проверки', error);
      setSubmissions([]);
      setReviewError(detailFromError(error, 'Не удалось загрузить очередь проверки.'));
    } finally {
      if (requestIsCurrent()) setReviewLoading(false);
    }
  }, [reviewEventId, reviewState]);

  const loadAchievements = useCallback(async () => {
    try {
      setAchievementsLoaded(false);
      setAchievementsLoading(true);
      setAchievementsError(null);
      const response = await api.get<EventAchievementOption[]>('/events/admin/achievements');
      setAchievements(response.data);
      setAchievementsLoaded(true);
    } catch (error: unknown) {
      logHttpError('Ошибка загрузки достижений для события', error);
      setAchievementsError(detailFromError(error, 'Не удалось загрузить достижения.'));
    } finally {
      setAchievementsLoading(false);
    }
  }, []);

  useEffect(() => { void loadEvents(); }, [loadEvents]);
  useEffect(() => { void loadReviewQueue(); }, [loadReviewQueue]);
  useEffect(() => {
    if (form.reward_type === 'achievement' && !achievementsLoaded && !achievementsLoading && !achievementsError) {
      void loadAchievements();
    }
  }, [achievementsError, achievementsLoaded, achievementsLoading, form.reward_type, loadAchievements]);

  const updateForm = <Key extends keyof EventFormValues>(key: Key, value: EventFormValues[Key]) => {
    setForm(current => ({ ...current, [key]: value }));
    setFormError(null);
  };

  const uploadCover = async (file: File) => {
    try {
      setUploadingCover(true);
      setCoverError(null);
      const body = new FormData();
      body.append('file', file);
      const response = await api.post<{ file_url: string }>('/events/admin/events/upload-cover', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateForm('cover_url', response.data.file_url);
      setCoverFile(null);
    } catch (error: unknown) {
      logHttpError('Ошибка загрузки обложки события', error);
      setCoverFile(file);
      setCoverError(detailFromError(error, 'Не удалось загрузить обложку.'));
    } finally {
      setUploadingCover(false);
    }
  };

  const validateForm = () => {
    if (!form.title.trim() || !form.description.trim() || !form.instructions.trim()) return 'Заполните название, описание и инструкции';
    if (!taskTypes.some(type => type.value === form.task_type)) return 'Выберите допустимый тип задания';
    if (!['coins', 'achievement'].includes(form.reward_type)) return 'Выберите допустимый тип награды';
    const startsAt = new Date(form.start_at);
    const endsAt = new Date(form.end_at);
    if (!form.start_at || !form.end_at || Number.isNaN(startsAt.valueOf()) || Number.isNaN(endsAt.valueOf()) || startsAt >= endsAt) {
      return 'Дата начала должна быть раньше даты окончания';
    }
    if (form.reward_type === 'coins' && (!Number.isInteger(Number(form.reward_amount)) || Number(form.reward_amount) <= 0)) {
      return 'Укажите положительное количество монет';
    }
    if (form.reward_type === 'achievement' && (!Number.isInteger(Number(form.reward_achievement_id)) || Number(form.reward_achievement_id) <= 0)) {
      return 'Выберите достижение для награды';
    }
    return null;
  };

  const saveEvent = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    const payload = {
      title: form.title.trim(), description: form.description.trim(), instructions: form.instructions.trim(),
      cover_url: form.cover_url, task_type: form.task_type,
      start_at: parseDateTimeLocalToUtc(form.start_at), end_at: parseDateTimeLocalToUtc(form.end_at),
      reward_type: form.reward_type,
      reward_amount: form.reward_type === 'coins' ? Number(form.reward_amount) : null,
      reward_achievement_id: form.reward_type === 'achievement' ? Number(form.reward_achievement_id) : null,
    };
    try {
      setSaving(true);
      setFormError(null);
      if (editingEvent) await api.put(`/events/admin/events/${editingEvent.id}`, payload);
      else await api.post('/events/admin/events', payload);
      setEditingEvent(null);
      setForm(emptyForm());
      setCoverFile(null);
      await loadEvents();
    } catch (error: unknown) {
      logHttpError('Ошибка сохранения события', error);
      setFormError(detailFromError(error, 'Не удалось сохранить событие.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmLifecycle = async () => {
    if (!pendingAction) return;
    const { action, event } = pendingAction;
    try {
      setLifecycleLoading(true);
      setFormError(null);
      if (action === 'delete') await api.delete(`/events/admin/events/${event.id}`);
      else await api.post(`/events/admin/events/${event.id}/${action}`);
      setPendingAction(null);
      await loadEvents();
      await loadReviewQueue();
    } catch (error: unknown) {
      logHttpError('Ошибка изменения состояния события', error);
      setFormError(detailFromError(error, 'Не удалось изменить состояние события.'));
    } finally {
      setLifecycleLoading(false);
    }
  };

  const reviewSubmission = async (item: EventSubmission, decision: 'accepted' | 'rejected') => {
    const note = (reviewNotes[item.id] ?? '').trim();
    if (decision === 'rejected' && !note) {
      setReviewError('Укажите причину отклонения');
      return;
    }
    if (decision === 'accepted') setAcceptedIds(current => new Set(current).add(item.id));
    try {
      setReviewingId(item.id);
      setReviewError(null);
      await api.post(`/events/admin/submissions/${item.id}/review`, { decision, note: decision === 'rejected' ? note : null });
      await loadReviewQueue();
    } catch (error: unknown) {
      logHttpError('Ошибка проверки работы события', error);
      if (decision === 'accepted') setAcceptedIds(current => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
      setReviewError(detailFromError(error, 'Не удалось проверить работу.'));
    } finally {
      setReviewingId(null);
    }
  };

  const startEdit = (item: EventSummary) => {
    if (item.state !== 'draft') return;
    setEditingEvent(item);
    setForm(fromEvent(item));
    setCoverError(null);
    setFormError(null);
    if (item.reward_type === 'achievement') void loadAchievements();
  };

  const lifecycleTitle: Record<LifecycleAction, string> = {
    publish: 'Опубликовать событие', close: 'Закрыть событие', cancel: 'Отменить событие', delete: 'Удалить черновик',
  };

  return (
    <section className="space-y-5" aria-labelledby="events-admin-heading">
      <div className="flex flex-col gap-4 rounded-2xl border border-gray-800 bg-[#1a1d24] p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 id="events-admin-heading" className="text-xl font-bold text-white">Управление событиями</h2>
          <p className="mt-1 text-sm text-gray-400">Создавайте события, управляйте их жизненным циклом и проверяйте работы.</p>
        </div>
        <button type="button" onClick={() => void (mode === 'manage' ? loadEvents() : loadReviewQueue())} className="rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-700">
          🔄 Обновить
        </button>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Разделы событий">
        <button type="button" role="tab" aria-selected={mode === 'manage'} onClick={() => setMode('manage')} className={`rounded-lg border px-4 py-2 text-sm font-bold transition ${mode === 'manage' ? 'border-red-500/50 bg-red-500/20 text-red-300' : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>События</button>
        <button type="button" role="tab" aria-selected={mode === 'review'} onClick={() => setMode('review')} className={`rounded-lg border px-4 py-2 text-sm font-bold transition ${mode === 'review' ? 'border-red-500/50 bg-red-500/20 text-red-300' : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>Проверка работ</button>
      </div>

      {mode === 'manage' ? <>
        {formError && <div role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{formError}</div>}
        <form noValidate className="rounded-2xl border border-gray-800 bg-[#1a1d24] p-5" onSubmit={(event) => { event.preventDefault(); void saveEvent(); }}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2"><h3 className="text-lg font-bold text-white">{editingEvent ? `Редактирование: ${editingEvent.title}` : 'Новый черновик'}</h3>{editingEvent && <button type="button" onClick={() => { setEditingEvent(null); setForm(emptyForm()); }} className="text-sm font-bold text-gray-300 hover:text-white">Новый черновик</button>}</div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm text-gray-300">Название события<input aria-label="Название события" value={form.title} onChange={(input) => updateForm('title', input.target.value)} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white" /></label>
            <label className="block text-sm text-gray-300">Тип задания<select aria-label="Тип задания" value={form.task_type} disabled={Boolean(editingEvent?.participant_count)} onChange={(input) => updateForm('task_type', input.target.value as EventTaskType)} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white disabled:cursor-not-allowed disabled:opacity-50">{taskTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}</select>{editingEvent?.participant_count ? <span className="mt-1 block text-xs text-amber-300">Тип задания заблокирован: есть участники.</span> : null}</label>
            <label className="block text-sm text-gray-300 md:col-span-2">Описание события<textarea aria-label="Описание события" value={form.description} onChange={(input) => updateForm('description', input.target.value)} className="mt-1 min-h-24 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white" /></label>
            <label className="block text-sm text-gray-300 md:col-span-2">Инструкции для участника<textarea aria-label="Инструкции для участника" value={form.instructions} onChange={(input) => updateForm('instructions', input.target.value)} className="mt-1 min-h-24 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white" /></label>
            <label className="block text-sm text-gray-300">Начало события<input aria-label="Начало события" type="datetime-local" value={form.start_at} onChange={(input) => updateForm('start_at', input.target.value)} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white" /></label>
            <label className="block text-sm text-gray-300">Окончание события<input aria-label="Окончание события" type="datetime-local" value={form.end_at} onChange={(input) => updateForm('end_at', input.target.value)} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white" /></label>
          </div>
          <fieldset className="mt-4"><legend className="text-sm text-gray-300">Награда</legend><div className="mt-2 flex flex-wrap gap-3"><label className="flex items-center gap-2 text-sm text-gray-300"><input aria-label="Награда монетами" type="radio" checked={form.reward_type === 'coins'} onChange={() => updateForm('reward_type', 'coins')} />Монеты</label><label className="flex items-center gap-2 text-sm text-gray-300"><input aria-label="Награда достижением" type="radio" checked={form.reward_type === 'achievement'} onChange={() => { setAchievementsLoaded(false); setAchievementsError(null); updateForm('reward_type', 'achievement'); }} />Достижение</label></div></fieldset>
          {form.reward_type === 'coins' ? <label className="mt-3 block text-sm text-gray-300">Количество монет<input aria-label="Количество монет" type="number" min="1" value={form.reward_amount} onChange={(input) => updateForm('reward_amount', input.target.value)} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white" /></label> : <div className="mt-3">{achievementsError && <div role="alert" className="mb-2 text-sm text-red-300">{achievementsError} <button type="button" onClick={() => void loadAchievements()} className="underline">Повторить загрузку достижений</button></div>}{achievementsLoaded && achievements.length === 0 && !achievementsError && <p className="mb-2 text-sm text-gray-400"><span>Каталог достижений пуст.</span> <button type="button" onClick={() => void loadAchievements()} className="underline">Повторить загрузку достижений</button></p>}<label className="block text-sm text-gray-300">Достижение для награды<select aria-label="Достижение для награды" value={form.reward_achievement_id} onChange={(input) => updateForm('reward_achievement_id', input.target.value)} disabled={achievementsLoading} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white"><option value="">Выберите достижение</option>{achievements.map(item => <option key={item.id} value={item.id}>{item.icon ?? '🏅'} {item.name}{item.description ? ` — ${item.description}` : ''}</option>)}</select></label></div>}
          <div className="mt-4 rounded-xl border border-gray-700 p-3"><label className="block text-sm text-gray-300">Обложка события<input aria-label="Обложка события" type="file" accept="image/png,image/jpeg,image/gif,image/webp" disabled={uploadingCover} onChange={(input) => { const file = input.target.files?.[0]; if (file) void uploadCover(file); }} className="mt-2 block w-full text-sm text-gray-300" /></label>{coverError && <div role="alert" className="mt-2 text-sm text-red-300">{coverError} {coverFile && <button type="button" onClick={() => void uploadCover(coverFile)} className="ml-2 underline">Повторить загрузку обложки</button>}</div>}{form.cover_url && <img src={`${API_BASE}${form.cover_url}`} alt="Предпросмотр обложки события" className="mt-3 h-32 w-full rounded-xl object-cover" />}</div>
          <button type="submit" disabled={saving || uploadingCover} className="mt-5 rounded-xl bg-red-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-400 disabled:opacity-50">{saving ? 'Сохраняем…' : editingEvent ? 'Сохранить событие' : 'Создать черновик'}</button>
        </form>
        {eventsLoading ? <div className="py-10 text-center text-gray-400">Загружаем события…</div> : eventsError ? <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 text-center"><p role="alert" className="text-red-300">{eventsError}</p><button type="button" onClick={() => void loadEvents()} className="mt-3 rounded-xl border border-red-500/30 px-4 py-2 text-sm font-bold text-red-200">Повторить загрузку событий</button></div> : events.length === 0 ? <div className="rounded-2xl border border-gray-800 bg-[#1a1d24] p-8 text-center text-gray-400">Событий пока нет.</div> : <div className="space-y-3">{events.map(item => <article key={item.id} className="rounded-2xl border border-gray-800 bg-[#1a1d24] p-5"><div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"><div><div className="flex flex-wrap items-center gap-2"><h3 className="text-lg font-bold text-white">{item.title}</h3><span className="rounded-full bg-gray-800 px-2 py-1 text-xs text-gray-300">{eventStates[item.state]}</span></div><p className="mt-1 text-sm text-gray-400">Участников: {item.participant_count} · до {formatDate(item.end_at)}</p></div><div className="flex flex-wrap gap-2">{item.state === 'draft' && <button type="button" onClick={() => startEdit(item)} aria-label={`Редактировать событие ${item.title}`} className="rounded-lg border border-gray-700 px-3 py-2 text-sm font-bold text-gray-200">Редактировать</button>}{item.state === 'draft' && <button type="button" onClick={() => setPendingAction({ action: 'publish', event: item })} aria-label={`Опубликовать событие ${item.title}`} className="rounded-lg border border-green-500/30 px-3 py-2 text-sm font-bold text-green-300">Опубликовать</button>}{item.state === 'published' && <button type="button" onClick={() => setPendingAction({ action: 'close', event: item })} aria-label={`Закрыть событие ${item.title}`} className="rounded-lg border border-amber-500/30 px-3 py-2 text-sm font-bold text-amber-200">Закрыть</button>}{(item.state === 'draft' || item.state === 'published') && <button type="button" onClick={() => setPendingAction({ action: 'cancel', event: item })} aria-label={`Отменить событие ${item.title}`} className="rounded-lg border border-red-500/30 px-3 py-2 text-sm font-bold text-red-300">Отменить</button>}{item.state === 'draft' && item.participant_count === 0 && <button type="button" onClick={() => setPendingAction({ action: 'delete', event: item })} aria-label={`Удалить черновик ${item.title}`} className="rounded-lg border border-red-500/30 px-3 py-2 text-sm font-bold text-red-300">Удалить</button>}</div></div></article>)}</div>}
        {pendingAction && <div role="dialog" aria-modal="true" aria-label={lifecycleTitle[pendingAction.action]} className="rounded-2xl border border-amber-500/30 bg-[#1a1d24] p-5"><h3 className="font-bold text-white">{lifecycleTitle[pendingAction.action]}</h3><p className="mt-2 text-sm text-gray-300">Подтвердите действие для события «{pendingAction.event.title}».</p><div className="mt-4 flex gap-3"><button type="button" disabled={lifecycleLoading} onClick={() => void confirmLifecycle()} className="rounded-lg bg-red-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Подтвердить действие</button><button type="button" disabled={lifecycleLoading} onClick={() => setPendingAction(null)} className="rounded-lg border border-gray-700 px-4 py-2 text-sm font-bold text-gray-300">Отмена</button></div></div>}
      </> : <>
        <div className="grid gap-3 rounded-2xl border border-gray-800 bg-[#1a1d24] p-5 md:grid-cols-2"><label className="block text-sm text-gray-300">Статус работ<select aria-label="Статус работ" value={reviewState} onChange={(input) => setReviewState(input.target.value as EventSubmissionState)} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white">{Object.entries(submissionStates).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label className="block text-sm text-gray-300">Событие<select aria-label="Фильтр события для работ" value={reviewEventId} onChange={(input) => setReviewEventId(input.target.value)} className="mt-1 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white"><option value="">Все события</option>{events.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}</select></label></div>
        {!reviewLoading && reviewError && submissions.length > 0 && <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{reviewError}</p>}
        {reviewLoading ? <div className="py-10 text-center text-gray-400">Загружаем очередь проверки…</div> : reviewError && submissions.length === 0 ? <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 text-center"><p role="alert" className="text-red-300">{reviewError}</p><button type="button" onClick={() => void loadReviewQueue()} className="mt-3 rounded-xl border border-red-500/30 px-4 py-2 text-sm font-bold text-red-200">Повторить загрузку работ</button></div> : submissions.length === 0 ? <div className="rounded-2xl border border-gray-800 bg-[#1a1d24] p-8 text-center text-gray-400">Работ с таким фильтром нет.</div> : <div className="space-y-3">{submissions.map(item => <article key={item.id} className="rounded-2xl border border-gray-800 bg-[#1a1d24] p-5"><div className="flex flex-wrap items-center gap-2 text-sm"><span className="font-mono text-gray-500">#{item.id}</span><span className="font-bold text-red-300">@{item.participant_username}</span><span className="text-gray-400">{item.event_title}</span><span className="rounded-full bg-gray-800 px-2 py-1 text-xs text-gray-300">{submissionStates[item.state]}</span></div><p className="mt-2 text-xs text-gray-500">Отправлено: {formatDate(item.submitted_at)} · Проверено: {formatDate(item.reviewed_at)}</p>{item.task_type === 'text' && <p className="mt-3 whitespace-pre-wrap text-sm text-gray-200">{item.payload_text}</p>}{item.task_type === 'file' && item.file_url && (isImage(item.file_url) ? <img src={`${API_BASE}${item.file_url}`} alt={`Файл работы @${item.participant_username}`} className="mt-3 max-h-64 rounded-xl" /> : <a href={`${API_BASE}${item.file_url}`} className="mt-3 inline-block text-sm text-blue-300 underline">Открыть файл работы</a>)}{item.task_type === 'confirmation' && <p className="mt-3 text-sm text-gray-200">Подтверждение выполнения получено</p>}{item.review_note && <p className="mt-3 rounded-xl border border-gray-700 p-3 text-sm text-gray-300">Результат: {item.review_note}</p>}{item.state === 'submitted' && <div className="mt-4 flex flex-col gap-2"><label className="text-sm text-gray-300">Причина отклонения для работы #{item.id}<textarea aria-label={`Причина отклонения для работы #${item.id}`} value={reviewNotes[item.id] ?? ''} onChange={(input) => setReviewNotes(current => ({ ...current, [item.id]: input.target.value }))} className="mt-1 min-h-20 w-full rounded-xl border border-gray-700 bg-[#0f1115] p-3 text-white" /></label><div className="flex flex-wrap gap-2"><button type="button" onClick={() => void reviewSubmission(item, 'accepted')} disabled={reviewingId === item.id || acceptedIds.has(item.id)} aria-label={`Принять работу #${item.id}`} className="rounded-lg border border-green-500/30 px-3 py-2 text-sm font-bold text-green-300 disabled:opacity-50">Принять</button><button type="button" onClick={() => void reviewSubmission(item, 'rejected')} disabled={reviewingId === item.id} aria-label={`Отклонить работу #${item.id}`} className="rounded-lg border border-red-500/30 px-3 py-2 text-sm font-bold text-red-300 disabled:opacity-50">Отклонить</button></div></div>}</article>)}</div>}
      </>}
    </section>
  );
};

export default EventsPanel;
